// web/src/lib/zitchAgent/agent.ts —— 对外：createZitchAgent（WSS 连接 / 状态机 / 音频处理）
// 拆自 web/src/lib/zitchAgent.ts 的同名分区（内容原样搬运）；由 web/src/lib/zitchAgent.ts 以 export * 聚合，导出面对调用方不变。

import { Message } from '@arco-design/web-react';

import { applyCallState, resetLegs, type ZitchLegs } from './_legs';
import { parseFrom } from './_protocol';
import { WORKLET_SRC } from './_worklet';
import type { ZitchAgent, ZitchEvent } from './types';

/**
 * WSS 信令地址由调用方按运行期配置传入（store 取 `sys_setting.zitch_wss_url`，DB 可改免重建 web）；
 * 缺省回落构建期 VITE_ZITCH_WSS，再回落 `wss://<页面hostname>:8443`（LAN/测试可配 ws://）。
 */
export function createZitchAgent(onEvent: (ev: ZitchEvent) => void, wssUrl?: string): ZitchAgent {
  const zitchWss: string =
    (wssUrl || '').trim() || import.meta.env.VITE_ZITCH_WSS || `wss://${location.hostname}:8443`;
  let ws: WebSocket | null = null;
  let closed = false;
  /** 主动注销/被踢（close 时不向外上报连接断开，避免覆盖 store 复位） */
  let intentionalClose = false;
  let kicked = false;

  // 通话：客户腿 / 咨询专家腿分开记——两腿操作必须带 call 号，禁止用单字段猜
  const legs: ZitchLegs = {
    callId: 0,
    consultCallId: 0,
    inviteCallId: 0,
    inviteUp: false,
    outDialing: false,
    consultDialing: false,
    consultCanceling: false,
    consultCompleting: false,
    held: false,
  };
  let muted = false;

  // 登录
  let loginResolve: (() => void) | null = null;
  let loginReject: ((reason: string) => void) | null = null;

  // 媒体
  let audioCtx: AudioContext | null = null; // 单一音频上下文（采集 48k + 播放 worklet）
  let workletNode: AudioWorkletNode | null = null; // 采集 worklet
  let playNode: AudioWorkletNode | null = null; // 播放 worklet（连续重采样 + 抖动缓冲）
  let micStream: MediaStream | null = null;
  let micTrack: MediaStreamTrack | null = null;
  let acc: number[] = [];
  let seq = 0;
  let rxCSeq = -1; // 最近一次播放的媒体帧 seq（-1=未锚定），用于缺口补静音

  function emit(ev: ZitchEvent) {
    onEvent(ev);
  }

  function send(msg: unknown) {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
    }
  }

  // ---------- 媒体 ----------
  function stopMedia() {
    if (workletNode) {
      try {
        workletNode.disconnect();
      } catch {
        /* noop */
      }
    }
    workletNode = null;
    if (micStream) {
      micStream.getTracks().forEach((t) => t.stop());
      micStream = null;
    }
    micTrack = null;
    if (playNode) {
      try {
        playNode.disconnect();
      } catch {
        /* noop */
      }
      playNode = null;
    }
    if (audioCtx) {
      audioCtx.close().catch(() => undefined);
      audioCtx = null;
    }
    acc = [];
  }

  async function ensureMedia(): Promise<void> {
    if (audioCtx) return;
    try {
      const ctx = new AudioContext({ sampleRate: 48000 });
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
      });
      const blob = new Blob([WORKLET_SRC], { type: 'application/javascript' });
      await ctx.audioWorklet.addModule(URL.createObjectURL(blob));
      const src = ctx.createMediaStreamSource(stream);
      const node = new AudioWorkletNode(ctx, 'pcm-cap');
      src.connect(node);
      node.port.onmessage = (ev) => {
        const f32: Float32Array = ev.data;
        for (let i = 0; i < f32.length; i++) acc.push(f32[i]);
        // 48k → 8k：每 6 取 1（均值低通）；160 样本/帧
        while (acc.length >= 160 * 6) {
          const frame = new Int16Array(160);
          for (let i = 0; i < 160; i++) {
            let s = 0;
            for (let k = 0; k < 6; k++) s += acc[i * 6 + k];
            s /= 6;
            frame[i] = Math.max(-32768, Math.min(32767, Math.round(s * 32767)));
          }
          acc.splice(0, 160 * 6);
          sendMedia(frame);
        }
      };
      const pn = new AudioWorkletNode(ctx, 'pcm-play', {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [1],
      });
      pn.connect(ctx.destination);
      audioCtx = ctx;
      workletNode = node;
      playNode = pn;
      micStream = stream;
      micTrack = stream.getAudioTracks()[0] || null;
      if (micTrack) micTrack.enabled = !muted;
    } catch (e: any) {
      Message.error(`麦克风启动失败：${e?.message || e}`);
    }
  }

  function sendMedia(frame: Int16Array) {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const buf = new ArrayBuffer(4 + frame.length * 2);
    const v = new DataView(buf);
    v.setUint8(0, 1); // l16
    v.setUint16(2, seq++ & 0xffff);
    new Int16Array(buf, 4).set(frame);
    ws.send(buf);
  }

  // 播放：8k AudioBuffer 在 8k 播放上下文里排期，收尾由浏览器一次性重采样到设备率。
  // 早期实现用 6× 零阶保持（每样本复制 6 次到 48k），产生镜像/台阶失真（发闷、发毛、金属味）；
  // 逐帧 8k 缓冲在 48k 上下文播放也会在每块边界重置重采样器，故改用播放 worklet。
  // 时间对齐用帧头 16-bit seq（固定 20ms/160 样本，seq 即媒体时钟）：缺口补等长静音，
  // 避免丢帧后时间压缩/漂移；重复帧丢弃。抖动（TCP 队头阻塞/时延变化）由 worklet
  // 的目标缓冲平滑，与 seq 无关；落后过多由 worklet 丢旧帧压时延。
  function playPcm(frame: Int16Array, frameSeq: number) {
    if (!playNode) return;
    if (rxCSeq >= 0) {
      const gap = (frameSeq - rxCSeq) & 0xffff;
      if (gap === 0) return; // 重复帧
      if (gap > 1 && gap < 100) {
        playNode.port.postMessage(new Float32Array((gap - 1) * frame.length));
      }
      // gap 过大（重连/长断流）不补，直接以新 seq 重新锚定
    }
    rxCSeq = frameSeq;
    const f = new Float32Array(frame.length);
    for (let i = 0; i < frame.length; i++) f[i] = frame[i] / 32768;
    playNode.port.postMessage(f);
  }

  // ---------- 信令 ----------
  function onMessage(ev: MessageEvent) {
    if (typeof ev.data !== 'string') {
      // 二进制媒体帧 [codec][flags][seq:16 BE][payload]：seq 用于缺口补偿，L16 payload 从偏移 4 起
      try {
        const buf = ev.data as ArrayBuffer;
        if (buf.byteLength < 4) return;
        const frameSeq = new DataView(buf).getUint16(2);
        playPcm(new Int16Array(buf, 4), frameSeq);
      } catch {
        /* 忽略坏帧 */
      }
      return;
    }
    let m: any;
    try {
      m = JSON.parse(ev.data);
    } catch {
      return;
    }
    switch (m.type) {
      case 'login_ok':
        loginResolve?.();
        loginResolve = loginReject = null;
        emit({ kind: 'registered' });
        break;
      case 'login_err':
        loginReject?.(m.reason || 'bad credentials');
        loginResolve = loginReject = null;
        emit({ kind: 'login_err', reason: m.reason || 'bad credentials' });
        break;
      case 'kicked':
        kicked = true;
        emit({ kind: 'login_err', reason: 'kicked' });
        break;
      case 'incoming':
        // 排队来话在 busy 时不应出现；咨询第二路也不会走 incoming。
        // 无 kind=客户来话；kind=invite 是同事邀请，不当新客户弹屏。
        if (m.kind === 'invite') {
          if (!m.call) break;
          legs.inviteCallId = m.call;
          const note = typeof m.note === 'string' ? m.note.trim() : '';
          emit({ kind: 'invite-in', call: m.call, from: parseFrom(m.from), note: note || undefined });
          break;
        }
        if (legs.callId && m.call !== legs.callId) {
          console.warn('[zitch] busy 时收到排队 incoming，已忽略', m.call);
          break;
        }
        legs.callId = m.call;
        void ensureMedia();
        emit({ kind: 'ringing-in', call: m.call, remote: parseFrom(m.from) });
        break;
      case 'state':
        applyCallState(legs, m, emit, () => void ensureMedia(), stopMedia);
        if (legs.consultCanceling && legs.consultCallId) {
          send({ type: 'hangup', call: legs.consultCallId });
          legs.consultCanceling = false;
        }
        break;
      case 'dtmf':
        emit({ kind: 'dtmf', call: m.call, digit: m.digit });
        break;
      default:
        break;
    }
  }

  function onClose() {
    if (loginReject) {
      loginReject('connection closed');
      loginResolve = loginReject = null;
    }
    stopMedia();
    const hadCall = legs.callId || legs.consultCallId || legs.outDialing || legs.consultDialing;
    const endedCall = legs.callId;
    resetLegs(legs);
    ws = null;
    if (intentionalClose) return;
    // 意外断线：先清理通话，再复位软电话状态（被踢时 old 连接静默关闭，由新连接接管）
    if (hadCall) emit({ kind: 'ended', call: endedCall });
    if (!kicked) emit({ kind: 'login_err', reason: 'connection closed' });
  }

  return {
    register(extension, password) {
      return new Promise<void>((resolve, reject) => {
        if (closed) {
          reject('agent closed');
          return;
        }
        let done = false;
        loginResolve = () => {
          if (done) return;
          done = true;
          resolve();
        };
        loginReject = (reason: string) => {
          if (done) return;
          done = true;
          reject(new Error(reason));
          try {
            ws?.close();
          } catch {
            /* noop */
          }
        };
        intentionalClose = false;
        kicked = false;
        const sock = new WebSocket(zitchWss);
        sock.binaryType = 'arraybuffer';
        ws = sock;
        sock.onopen = () => {
          sock.send(JSON.stringify({ type: 'login', agent: extension, password, codec: 'l16' }));
        };
        sock.onmessage = onMessage;
        sock.onerror = () => {
          if (!done) {
            done = true;
            reject(new Error('websocket error'));
          }
        };
        sock.onclose = () => {
          onClose();
          if (!done) {
            done = true;
            reject(new Error('connection closed'));
          }
        };
      });
    },

    async unregister() {
      stopMedia();
      intentionalClose = true;
      if (ws) {
        try {
          ws.close();
        } catch {
          /* noop */
        }
        ws = null;
      }
      resetLegs(legs);
      muted = false;
    },

    async answer(call) {
      const id = call || legs.callId;
      if (!id) return;
      void ensureMedia();
      send({ type: 'answer', call: id });
    },

    async hangup(call) {
      // 咨询第二路不得裸 hangup：core 会回退到客户腿。客户出局未拿到号才省略 call。
      const id = call || legs.callId;
      if (id) {
        send({ type: 'hangup', call: id });
      } else if (legs.consultDialing) {
        legs.consultCanceling = true;
      } else if (legs.outDialing) {
        legs.outDialing = false;
        send({ type: 'hangup' });
      }
    },

    // 见 types.ts：服务端确认本腿已不在时本地收尾，不等引擎下行
    endLocal() {
      if (!legs.callId && !legs.outDialing && !legs.consultCallId && !legs.consultDialing) return; // 本机没有在途腿：不动界面（避免复位已结束/未开始的通）
      const c = legs.callId;
      // 专家号未到 / 邀请腿仍在：只收客户，保留第二路标记
      if (legs.consultCallId || legs.consultDialing || legs.consultCanceling || legs.inviteCallId) {
        legs.callId = 0;
        legs.outDialing = false;
        legs.held = false;
        stopMedia();
        if (c) emit({ kind: 'ended', call: c });
        return;
      }
      resetLegs(legs);
      stopMedia();
      emit({ kind: 'ended', call: c });
    },

    call(target) {
      legs.outDialing = true;
      void ensureMedia();
      send({ type: 'call', dest: target });
    },

    sendDtmf(tone) {
      if (!legs.callId) return;
      send({ type: 'dtmf', call: legs.callId, digit: tone });
    },

    setMuted(value: boolean) {
      muted = value;
      if (micTrack) micTrack.enabled = !value;
      // 服务端 mute 未上线：本地停麦作降级，对端仍可能漏音
      if (value) console.info('[zitch] 非服务端静音：仅停本机麦克风');
    },

    async toggleHold() {
      if (!legs.callId) return false;
      legs.held = !legs.held;
      send({ type: legs.held ? 'hold' : 'unhold', call: legs.callId });
      return legs.held;
    },

    async hold(call) {
      const id = call || legs.callId;
      if (!id) return false;
      send({ type: 'hold', call: id });
      if (id === legs.callId) legs.held = true;
      return true;
    },

    async unhold(call) {
      const id = call || legs.callId;
      if (!id) return false;
      send({ type: 'unhold', call: id });
      if (id === legs.callId) legs.held = false;
      return false;
    },

    refer(target) {
      if (!legs.callId) return;
      send({ type: 'transfer', call: legs.callId, dest: target });
    },

    inviteExpert(dest) {
      const t = (dest || '').trim();
      if (!t || !legs.callId || legs.consultCallId || legs.consultDialing || legs.consultCanceling) return;
      legs.consultDialing = true;
      send({ type: 'call', dest: t });
    },

    completeTransfer(customerCall, expertCall) {
      if (!customerCall || !expertCall) return;
      legs.consultCompleting = true;
      send({ type: 'transfer', call: customerCall, to_call: expertCall });
    },

    cancelConsult(expertCall, customerCall) {
      const expert = expertCall || legs.consultCallId;
      if (expert) send({ type: 'hangup', call: expert });
      else if (legs.consultDialing) legs.consultCanceling = true;
      if (customerCall) {
        send({ type: 'unhold', call: customerCall });
        legs.held = false;
      }
      // 不清咨询标记：专家 ended 仍须按咨询腿分流，不得 reset 客户腿
    },

    currentCall() {
      return legs.callId;
    },

    consultCall() {
      return legs.consultCallId;
    },

    consultPending() {
      return !!(legs.consultCallId || legs.consultDialing || legs.consultCanceling);
    },

    inviteCall() {
      return legs.inviteCallId;
    },

    isConnected() {
      return !!ws && ws.readyState === WebSocket.OPEN;
    },
  };
}
